// Copyright (c) 2026 Michael F. Kamprath
//
// settings-panel.js -- slide-in Settings drawer per docs/SETTINGS-SPEC.md.
//
// Self-contained: exports a factory that returns { panelElement, open,
// close, isOpen, destroy }.  Caller (src/app.js, via slice e.3) owns
// mounting the returned panelElement into the DOM and wiring it to
// the home-screen Settings button.
//
// Sections, top to bottom (SETTINGS-SPEC §3):
//   1. Gameplay  (§4)   -- max count, entrance anim, verbal prompts, fullscreen
//   2. Language  (§5)   -- locale picker + numeral script per locale
//   3. Themes    (§6)   -- 6-icon grid with min-one-active constraint
//   4. Support   (§7)   -- PayPal donation form w/ offline fallback
//   5. Privacy   (§8)   -- share-stats toggle + disclosure text
//   6. Credits   (§9)   -- static multi-block
//   7. About     (§10)  -- name, copyright, contact, privacy prose, version
//
// All setting changes flow through src/settings.js setters, never
// directly to localStorage (SETTINGS-SPEC §11).

import {
  getLocale, setLocale,
  getVerbalPromptMode, setVerbalPromptMode,
  getEntranceAnimations, setEntranceAnimations,
  getMaxCount, setMaxCount, getMaxCountBounds,
  getActiveThemes, setActiveThemes,
  getNumeralScript, setNumeralScript,
  getShareStats, setShareStats,
  SUPPORTED_LOCALES, VERBAL_PROMPT_MODES,
  NUMERAL_SCRIPT_OPTIONS_BY_LOCALE,
} from '../settings.js';
import { t } from './strings/index.js';
import { getOtherFamilyApps } from '../family-apps.js';

// --- Constants ------------------------------------------------------------

const PAYPAL_FORM_ACTION = 'https://www.paypal.com/ncp/payment/P8MTRAWLNQT8J';
const PAYPAL_CARDS_IMG  = 'https://www.paypalobjects.com/images/Debit_Credit_APM.svg';
const PAYPAL_WORDMARK   = 'https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-wordmark-color.svg';

/** Path where aggregate telemetry is visible (see SETTINGS-SPEC §8 + §10). */
const STATS_URL = '/stats';

/** Theme ids in the order they appear in the theme-picker grid. */
const THEME_IDS = Object.freeze(['sheep', 'cows', 'horses', 'chicks', 'apples', 'flowers']);

// --- DOM helpers ----------------------------------------------------------

function el(tag, className, attrs = {}) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  for (const [k, v] of Object.entries(attrs)) {
    if (v === false || v === null || v === undefined) continue;
    if (k === 'text') node.textContent = v;
    else if (k === 'html') node.innerHTML = v;
    else if (k in node && typeof node[k] !== 'function') node[k] = v;
    else node.setAttribute(k, v === true ? '' : String(v));
  }
  return node;
}

function section(id, headingKey, locale) {
  const sec = el('section', 'settings-section', { 'data-section': id });
  sec.appendChild(el('h3', 'settings-section__heading', { text: t(headingKey, locale) }));
  return sec;
}

function row(labelKey, control, locale) {
  const wrap = el('label', 'settings-row');
  wrap.appendChild(el('span', 'settings-row__label', { text: t(labelKey, locale) }));
  wrap.appendChild(control);
  return wrap;
}

// --- Feature detection ----------------------------------------------------

/**
 * True when the current platform supports the Fullscreen API on
 * element requests.  iOS Safari returns false here (the API is not
 * implemented on arbitrary elements); desktop + Android Chrome +
 * Firefox + desktop Safari all return true.
 */
function fullscreenApiAvailable() {
  return typeof document !== 'undefined'
    && typeof document.documentElement?.requestFullscreen === 'function';
}

function isFullscreenActive() {
  return !!(document?.fullscreenElement);
}

async function enterFullscreen() {
  try { await document.documentElement.requestFullscreen(); }
  catch { /* gesture may have expired or user cancelled; silent */ }
}

async function exitFullscreen() {
  try {
    if (document.exitFullscreen) await document.exitFullscreen();
  } catch { /* silent */ }
}

// --- Factory --------------------------------------------------------------

/**
 * @typedef {Object} SettingsPanelHandle
 * @property {HTMLElement} panelElement  Slide-in drawer (aside).
 * @property {() => void} open
 * @property {() => void} close
 * @property {() => boolean} isOpen
 * @property {() => void} destroy
 * @property {() => Array<string>} [__getActiveThemes]  Test hook.
 */

/**
 * Build a settings drawer.  Caller is responsible for inserting
 * `panelElement` into the DOM and linking it to a trigger button
 * (typically the home-screen Settings button in slice e.3).
 *
 * @param {{
 *   locale?: string,                  // UI string locale (defaults to 'en')
 *   availableLocales?: Array<string>, // which locales the picker lets you
 *                                     // select between.  Defaults to just
 *                                     // the current getLocale() entry --
 *                                     // Phase 3 flips this to SUPPORTED_LOCALES
 *                                     // when the non-en locales actually ship.
 *   version?: string,                 // app version string for About.
 *                                     // Defaults to import.meta.env.PACKAGE_VERSION.
 *   onlineProbe?: () => boolean,      // returns navigator.onLine by default;
 *                                     // tests inject a stub.
 *   onClose?: () => void,
 * }} [opts]
 * @returns {SettingsPanelHandle}
 */
export function createSettingsPanel(opts = {}) {
  const locale = opts.locale || 'en';
  const availableLocales = Array.isArray(opts.availableLocales) && opts.availableLocales.length > 0
    ? opts.availableLocales
    : [getLocale()];
  // Explicit opts.version wins (including the empty string, which means
  // "omit the Version line").  Fall through to the Vite-injected env
  // only when the key is entirely absent from opts.
  const version = Object.prototype.hasOwnProperty.call(opts, 'version')
    ? (typeof opts.version === 'string' ? opts.version : '')
    : (import.meta.env.PACKAGE_VERSION || '');
  const onlineProbe = typeof opts.onlineProbe === 'function'
    ? opts.onlineProbe
    : () => (typeof navigator !== 'undefined' ? !!navigator.onLine : true);
  const onClose = typeof opts.onClose === 'function' ? opts.onClose : () => {};

  // --- Root element + header ---------------------------------------------
  const panelElement = el('aside', 'settings-panel', {
    role: 'dialog',
    'aria-modal': 'false',
    'aria-label': t('settings.title', locale),
    hidden: true,
  });

  const header = el('header', 'settings-panel__header');
  header.appendChild(el('h2', 'settings-panel__title', {
    text: t('settings.title', locale),
  }));
  const closeBtn = el('button', 'settings-panel__close', {
    type: 'button',
    'aria-label': t('settings.close', locale),
    text: '\u00d7',   // ×
  });
  header.appendChild(closeBtn);
  panelElement.appendChild(header);

  const body = el('div', 'settings-panel__body');
  panelElement.appendChild(body);

  // --- Section 1: Gameplay -----------------------------------------------
  const gameplay = section('gameplay', 'settings.section.gameplay', locale);

  // Max count (slider)
  const { min: maxCountMin, max: maxCountMax } = getMaxCountBounds();
  const maxCountControl = el('span', 'settings-row__control');
  const maxCountInput = el('input', 'settings-range', {
    type: 'range',
    min: String(maxCountMin),
    max: String(maxCountMax),
    step: '1',
    value: String(getMaxCount()),
    'aria-label': t('settings.maxCount', locale),
  });
  const maxCountReadout = el('span', 'settings-range__readout', {
    text: String(getMaxCount()),
  });
  maxCountControl.appendChild(maxCountInput);
  maxCountControl.appendChild(maxCountReadout);
  maxCountInput.addEventListener('input', () => {
    const v = parseInt(maxCountInput.value, 10);
    if (Number.isFinite(v)) {
      setMaxCount(v);
      maxCountReadout.textContent = String(getMaxCount());
    }
  });
  gameplay.appendChild(row('settings.maxCount', maxCountControl, locale));

  // Entrance animations (toggle)
  const entranceToggle = el('input', 'settings-toggle__input', {
    type: 'checkbox',
    checked: getEntranceAnimations(),
    'aria-label': t('settings.entranceAnimations', locale),
  });
  entranceToggle.addEventListener('change', () => {
    setEntranceAnimations(entranceToggle.checked);
  });
  gameplay.appendChild(row('settings.entranceAnimations', entranceToggle, locale));

  // Verbal prompts -- three-way segmented control (spec §4.3 amended:
  // all / counting-only / off).  Pre-SETTINGS-SPEC was a binary toggle;
  // splitting it out lets the parent keep the counting voice on tap
  // while silencing the intros / praise / comparator / assist prompts.
  const verbalRow = el('div', 'settings-row settings-row--verbal-prompts');
  verbalRow.appendChild(el('span', 'settings-row__label', {
    text: t('settings.verbalPrompts', locale),
  }));
  const verbalGroup = el('div', 'settings-segmented', {
    role: 'radiogroup',
    'aria-label': t('settings.verbalPrompts', locale),
  });
  /** @type {Map<string, HTMLButtonElement>} */
  const verbalButtons = new Map();
  for (const mode of VERBAL_PROMPT_MODES) {
    const btn = el('button', 'settings-segmented__option', {
      type: 'button',
      role: 'radio',
      'data-value': mode,
      text: t(`settings.verbalPromptMode.${mode}`, locale),
    });
    btn.addEventListener('click', () => {
      setVerbalPromptMode(mode);
      syncVerbalButtons();
    });
    verbalButtons.set(mode, btn);
    verbalGroup.appendChild(btn);
  }
  verbalRow.appendChild(verbalGroup);
  function syncVerbalButtons() {
    const current = getVerbalPromptMode();
    for (const [mode, btn] of verbalButtons) {
      const on = mode === current;
      btn.setAttribute('aria-checked', String(on));
      btn.classList.toggle('settings-segmented__option--selected', on);
    }
  }
  syncVerbalButtons();
  gameplay.appendChild(verbalRow);

  // Fullscreen (toggle) OR iOS hint
  const fullscreenSupported = fullscreenApiAvailable();
  let fullscreenToggle = null;
  let fullscreenChangeHandler = null;
  if (fullscreenSupported) {
    fullscreenToggle = el('input', 'settings-toggle__input', {
      type: 'checkbox',
      checked: isFullscreenActive(),
      'aria-label': t('settings.fullscreen', locale),
    });
    fullscreenToggle.addEventListener('change', () => {
      if (fullscreenToggle.checked) enterFullscreen();
      else exitFullscreen();
    });
    // Keep the toggle in sync when the user presses Esc to exit, etc.
    fullscreenChangeHandler = () => {
      fullscreenToggle.checked = isFullscreenActive();
    };
    document.addEventListener('fullscreenchange', fullscreenChangeHandler);
    gameplay.appendChild(row('settings.fullscreen', fullscreenToggle, locale));
  } else {
    // iOS Safari and other platforms without the API -- spec §4.4.
    // Show an instructional paragraph instead of a broken toggle.
    const hint = el('p', 'settings-hint', {
      'data-hint': 'fullscreen-ios',
      text: t('settings.fullscreen.iosHint', locale),
    });
    gameplay.appendChild(hint);
  }
  body.appendChild(gameplay);

  // --- Section 2: Language -----------------------------------------------
  const languageSec = section('language', 'settings.section.language', locale);

  // Locale dropdown.
  const localeSelect = el('select', 'settings-select', {
    'aria-label': t('settings.language', locale),
  });
  for (const id of SUPPORTED_LOCALES) {
    // Include every supported locale in the menu -- spec §5.1 lists
    // them all regardless of bundling -- but disable the whole
    // control when only one is actually available so the child /
    // parent can't select an unbundled locale.
    const opt = el('option', null, {
      value: id,
      text: t(`settings.language.locale.${id}`, locale),
    });
    if (id === getLocale()) opt.selected = true;
    localeSelect.appendChild(opt);
  }
  if (availableLocales.length <= 1) {
    localeSelect.disabled = true;
    localeSelect.title = '';   // no reason string; the disabled styling speaks for itself
  }
  localeSelect.addEventListener('change', () => {
    setLocale(localeSelect.value);
    // Keep the numeral-script control in sync with the new locale.
    renderNumeralScriptControl();
  });
  languageSec.appendChild(row('settings.language', localeSelect, locale));

  // Numeral script segmented control -- hidden when the locale has only
  // one option.  Rebuilt on every locale change.
  const numeralScriptRow = el('div', 'settings-row settings-row--numeral-script');
  languageSec.appendChild(numeralScriptRow);

  function renderNumeralScriptControl() {
    numeralScriptRow.innerHTML = '';
    const currentLocale = getLocale();
    const options = NUMERAL_SCRIPT_OPTIONS_BY_LOCALE[currentLocale] || [];
    if (options.length <= 1) {
      numeralScriptRow.hidden = true;
      return;
    }
    numeralScriptRow.hidden = false;
    numeralScriptRow.appendChild(el('span', 'settings-row__label', {
      text: t('settings.numeralScript', locale),
    }));
    const group = el('div', 'settings-segmented', {
      role: 'radiogroup',
      'aria-label': t('settings.numeralScript', locale),
    });
    const current = getNumeralScript(currentLocale);
    for (const opt of options) {
      const btn = el('button', 'settings-segmented__option', {
        type: 'button',
        role: 'radio',
        'aria-checked': String(opt === current),
        'data-value': opt,
        text: t(`settings.numeralScript.${opt}`, locale),
      });
      if (opt === current) btn.classList.add('settings-segmented__option--selected');
      btn.addEventListener('click', () => {
        setNumeralScript(opt, currentLocale);
        // Re-render to reflect the new selection.  Cheap; radio-group sizes
        // are small.
        renderNumeralScriptControl();
      });
      group.appendChild(btn);
    }
    numeralScriptRow.appendChild(group);
  }
  renderNumeralScriptControl();

  body.appendChild(languageSec);

  // --- Section 3: Themes -------------------------------------------------
  const themesSec = section('themes', 'settings.section.themes', locale);
  themesSec.appendChild(el('p', 'settings-hint', {
    text: t('settings.themes.hint', locale),
  }));
  const themeGrid = el('div', 'settings-theme-grid', {
    role: 'group',
    'aria-label': t('settings.section.themes', locale),
  });
  /** @type {Map<string, HTMLElement>} */
  const themeTiles = new Map();
  for (const themeId of THEME_IDS) {
    const tile = el('button', 'settings-theme-tile', {
      type: 'button',
      'data-theme': themeId,
      'aria-pressed': 'false',
      title: t(`settings.theme.${themeId}`, locale),
    });
    const img = el('img', 'settings-theme-tile__icon', {
      src: `/images/ui/theme-icons/head_${themeId}.webp`,
      alt: t(`settings.theme.${themeId}`, locale),
      width: 64,
      height: 64,
      draggable: false,
    });
    tile.appendChild(img);
    tile.appendChild(el('span', 'settings-theme-tile__label', {
      text: t(`settings.theme.${themeId}`, locale),
    }));
    tile.addEventListener('click', () => {
      const active = new Set(getActiveThemes());
      if (active.has(themeId)) {
        // Enforce min-one constraint: block the un-toggle if this is
        // the last active theme.
        if (active.size <= 1) {
          tile.title = t('settings.themes.minOneTooltip', locale);
          return;
        }
        active.delete(themeId);
      } else {
        active.add(themeId);
      }
      setActiveThemes(THEME_IDS.filter((id) => active.has(id)));
      syncThemeTiles();
    });
    themeTiles.set(themeId, tile);
    themeGrid.appendChild(tile);
  }
  themesSec.appendChild(themeGrid);
  function syncThemeTiles() {
    const active = new Set(getActiveThemes());
    for (const [id, tile] of themeTiles) {
      const on = active.has(id);
      tile.setAttribute('aria-pressed', on ? 'true' : 'false');
      tile.classList.toggle('settings-theme-tile--active', on);
      tile.classList.toggle('settings-theme-tile--inactive', !on);
      // When exactly one is on, that tile becomes visually locked
      // (the click handler no-ops but the cursor feedback helps).
      const isLastActive = on && active.size === 1;
      tile.classList.toggle('settings-theme-tile--locked', isLastActive);
      tile.title = isLastActive
        ? t('settings.themes.minOneTooltip', locale)
        : t(`settings.theme.${id}`, locale);
    }
  }
  syncThemeTiles();
  body.appendChild(themesSec);

  // --- Section 4: Support ------------------------------------------------
  const supportSec = section('support', 'settings.section.support', locale);
  supportSec.appendChild(el('p', 'settings-hint', {
    text: t('settings.support.description', locale),
  }));

  // Online form (hidden when offline).
  const paypalForm = el('form', 'settings-paypal', {
    action: PAYPAL_FORM_ACTION,
    method: 'post',
    target: '_blank',
    rel: 'noopener noreferrer',
  });
  const paypalButton = el('button', 'settings-paypal__button', {
    type: 'submit',
    text: t('settings.support.donate', locale),
  });
  paypalForm.appendChild(paypalButton);
  paypalForm.appendChild(el('img', 'settings-paypal__cards', {
    src: PAYPAL_CARDS_IMG,
    alt: '',
    height: 24,
  }));
  paypalForm.appendChild(el('img', 'settings-paypal__wordmark', {
    src: PAYPAL_WORDMARK,
    alt: t('settings.support.poweredBy', locale),
    height: 18,
  }));
  supportSec.appendChild(paypalForm);

  // Offline fallback.
  const offlineNotice = el('p', 'settings-hint settings-hint--offline', {
    text: t('settings.support.offline', locale),
  });
  offlineNotice.hidden = true;
  supportSec.appendChild(offlineNotice);

  function syncOnlineState() {
    const online = onlineProbe();
    panelElement.setAttribute('data-online', online ? 'true' : 'false');
    paypalForm.hidden = !online;
    offlineNotice.hidden = online;
  }
  syncOnlineState();
  function handleOnline()  { syncOnlineState(); }
  function handleOffline() { syncOnlineState(); }
  if (typeof window !== 'undefined') {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
  }
  body.appendChild(supportSec);

  // --- Section 4.5: Family cross-promo -----------------------------------
  // Small list of sibling Ewe apps rendered as icon+tagline tiles.
  // Each tile is an anchor that opens the target site in a new tab
  // with the same `rel="noopener noreferrer"` hardening as the PayPal
  // form and the stats link. The list comes from ../family-apps.js
  // with self filtered out, so this app never links to itself; adding
  // a new family member is a one-file change there.
  //
  // Offline handling mirrors the Support section: when navigator
  // flips offline, the tile list hides and a short hint takes its
  // place. Without it, taps would open a new tab that lands on the
  // browser's offline-error page — worse than a silent "you're
  // offline" message.
  const familySec = section('family', 'settings.section.family', locale);
  familySec.appendChild(el('p', 'settings-hint', {
    text: t('settings.family.body', locale),
  }));

  const familyList = el('ul', 'settings-family__list');
  for (const app of getOtherFamilyApps()) {
    const item = el('li', 'settings-family__item');
    const tile = el('a', 'settings-family__tile', {
      href: app.url,
      target: '_blank',
      rel: 'noopener noreferrer',
      'data-app-id': app.id,
    });
    tile.appendChild(el('img', 'settings-family__icon', {
      src: app.icon,
      alt: '',
      width: 64,
      height: 64,
      loading: 'lazy',
    }));
    const textWrap = el('span', 'settings-family__text');
    textWrap.appendChild(el('span', 'settings-family__name', {
      text: app.name,
    }));
    textWrap.appendChild(el('span', 'settings-family__tagline', {
      text: t(app.taglineKey, locale),
    }));
    tile.appendChild(textWrap);
    item.appendChild(tile);
    familyList.appendChild(item);
  }
  familySec.appendChild(familyList);

  const familyOfflineNotice = el('p', 'settings-hint settings-hint--offline', {
    text: t('settings.family.offline', locale),
  });
  familyOfflineNotice.hidden = true;
  familySec.appendChild(familyOfflineNotice);

  // Dedicated online/offline handling for the family section. We use
  // the same `onlineProbe()` that Support uses so both sections stay
  // in sync, but register our own listeners so destroy() can clean
  // them up independently of Support's handlers (and so that future
  // changes to the Support section's sync don't silently break ours).
  function syncFamilyOnlineState() {
    const online = onlineProbe();
    familyList.hidden = !online;
    familyOfflineNotice.hidden = online;
  }
  syncFamilyOnlineState();
  function handleFamilyOnline()  { syncFamilyOnlineState(); }
  function handleFamilyOffline() { syncFamilyOnlineState(); }
  if (typeof window !== 'undefined') {
    window.addEventListener('online',  handleFamilyOnline);
    window.addEventListener('offline', handleFamilyOffline);
  }

  body.appendChild(familySec);

  // --- Section 5: Privacy ------------------------------------------------
  const privacySec = section('privacy', 'settings.section.privacy', locale);
  const shareStatsToggle = el('input', 'settings-toggle__input', {
    type: 'checkbox',
    checked: getShareStats(),
    'aria-label': t('settings.privacy.shareStats', locale),
  });
  shareStatsToggle.addEventListener('change', () => {
    setShareStats(shareStatsToggle.checked);
  });
  privacySec.appendChild(row('settings.privacy.shareStats', shareStatsToggle, locale));

  const disclosure = el('p', 'settings-hint settings-hint--disclosure');
  disclosure.appendChild(document.createTextNode(t('settings.privacy.disclosurePrefix', locale)));
  disclosure.appendChild(el('a', 'settings-link', {
    href: STATS_URL,
    target: '_blank',
    rel: 'noopener noreferrer',
    text: t('settings.privacy.viewStats', locale),
  }));
  privacySec.appendChild(disclosure);
  body.appendChild(privacySec);

  // --- Section 6: Credits ------------------------------------------------
  const creditsSec = section('credits', 'settings.section.credits', locale);
  function creditBlock(headingKey, peopleKey, urlKey) {
    const block = el('div', 'settings-credit-block');
    block.appendChild(el('h4', 'settings-credit-block__heading', {
      text: t(headingKey, locale),
    }));
    block.appendChild(el('p', 'settings-credit-block__people', {
      text: t(peopleKey, locale),
    }));
    if (urlKey) {
      block.appendChild(el('p', 'settings-credit-block__url', {
        text: t(urlKey, locale),
      }));
    }
    return block;
  }
  creditsSec.appendChild(creditBlock(
    'settings.credits.art.heading',
    'settings.credits.art.name',
  ));
  creditsSec.appendChild(creditBlock(
    'settings.credits.voices.en.heading',
    'settings.credits.voices.en.people',
  ));
  creditsSec.appendChild(creditBlock(
    'settings.credits.voices.zh.heading',
    'settings.credits.voices.zh.people',
  ));
  creditsSec.appendChild(creditBlock(
    'settings.credits.voices.ja.heading',
    'settings.credits.voices.ja.people',
  ));
  creditsSec.appendChild(creditBlock(
    'settings.credits.voices.ko.heading',
    'settings.credits.voices.ko.people',
  ));

  // Music block has multiple entries, each Title / © Publisher.
  const musicBlock = el('div', 'settings-credit-block settings-credit-block--music');
  musicBlock.appendChild(el('h4', 'settings-credit-block__heading', {
    text: t('settings.credits.music.heading', locale),
  }));
  for (let i = 1; i <= 5; i++) {
    const entry = el('p', 'settings-credit-music-entry');
    entry.appendChild(el('span', 'settings-credit-music-entry__title', {
      text: '\u201c' + t(`settings.credits.music.${i}.title`, locale) + '\u201d',
    }));
    entry.appendChild(document.createTextNode(' \u00a9 '));
    entry.appendChild(el('span', 'settings-credit-music-entry__publisher', {
      text: t(`settings.credits.music.${i}.publisher`, locale),
    }));
    musicBlock.appendChild(entry);
  }
  creditsSec.appendChild(musicBlock);
  body.appendChild(creditsSec);

  // --- Section 7: About --------------------------------------------------
  const aboutSec = section('about', 'settings.section.about', locale);
  aboutSec.appendChild(el('p', 'settings-about__appname', {
    text: t('settings.about.appName', locale),
  }));
  aboutSec.appendChild(el('p', 'settings-about__copyright', {
    text: t('settings.about.copyright', locale),
  }));
  const contactRow = el('p', 'settings-about__contact');
  // target=_blank on a mailto is a minor no-op in most browsers (the
  // email client handles the handoff regardless), but adding it here
  // keeps the invariant "every clickable link in the settings panel
  // opens in a new tab" uniform -- so the regression test below can
  // rely on a single querySelectorAll('a').
  contactRow.appendChild(el('a', 'settings-link', {
    href: `mailto:${t('settings.about.contact', locale)}`,
    target: '_blank',
    rel: 'noopener noreferrer',
    text: t('settings.about.contact', locale),
  }));
  aboutSec.appendChild(contactRow);

  aboutSec.appendChild(el('h4', 'settings-about__privacy-heading', {
    text: t('settings.about.privacyHeading', locale),
  }));
  aboutSec.appendChild(el('p', 'settings-about__privacy', {
    text: t('settings.about.privacyParagraph1', locale),
  }));
  const privacyPara2 = el('p', 'settings-about__privacy');
  privacyPara2.appendChild(document.createTextNode(
    t('settings.about.privacyParagraph2Prefix', locale),
  ));
  privacyPara2.appendChild(el('a', 'settings-link', {
    href: STATS_URL,
    target: '_blank',
    rel: 'noopener noreferrer',
    text: t('settings.about.privacyStatsLinkText', locale),
  }));
  privacyPara2.appendChild(document.createTextNode(
    t('settings.about.privacyParagraph2Suffix', locale),
  ));
  aboutSec.appendChild(privacyPara2);

  if (version) {
    aboutSec.appendChild(el('p', 'settings-about__version', {
      text: `${t('settings.about.versionLabel', locale)}: ${version}`,
    }));
  }
  body.appendChild(aboutSec);

  // --- State + lifecycle -------------------------------------------------
  let open = false;

  /**
   * Element that had keyboard focus immediately before the panel
   * opened.  Captured in setOpen(true) so we can return focus to it
   * when the panel closes -- standard modal-dialog pattern (WCAG
   * 2.4.3 Focus Order, Level A; 3.2.1 On Focus, Level A).  Without
   * this, a keyboard parent who tabs from the gear button into the
   * panel and then ESC-closes ends up on `<body>` and has to start
   * navigation from scratch.
   *
   * Cleared by destroy() before its setOpen(false) call so the
   * locale-change rebuild path doesn't try to focus the about-to-be-
   * destroyed gear button (which would flicker focus and produce a
   * stale screen-reader announcement).
   */
  let previouslyFocused = null;

  function setOpen(next) {
    if (open === next) return;
    const wasOpen = open;
    open = next;
    panelElement.hidden = !open;
    panelElement.classList.toggle('settings-panel--open', open);
    if (typeof document !== 'undefined') {
      if (open) document.documentElement.setAttribute('data-settings-open', '');
      else document.documentElement.removeAttribute('data-settings-open');
    }
    if (open && fullscreenToggle) {
      // `fullscreenchange` only fires while the panel is mounted -- if
      // the user pressed Esc while the panel was closed, the toggle's
      // checked state is stale.  Resync on every open.
      fullscreenToggle.checked = isFullscreenActive();
    }

    // --- Focus management ---------------------------------------------
    // Opening: stash the currently-focused element so we can restore
    // it on close, then move focus to the close button so a screen
    // reader announces "Settings dialog, Close button" rather than
    // staying silent.  Closing: restore focus to the previously-
    // focused element if it's still in the DOM and focusable, else
    // drop it on document.body (browser default).
    //
    // Wrapped in try/catch because focus() can throw in some test
    // environments and on iOS Safari for elements that aren't quite
    // ready -- the focus is a UX nicety, not load-bearing.
    if (typeof document !== 'undefined') {
      if (open && !wasOpen) {
        previouslyFocused = (document.activeElement instanceof HTMLElement)
          ? document.activeElement
          : null;
        try { closeBtn.focus(); } catch { /* non-fatal */ }
      } else if (!open && wasOpen) {
        const target = previouslyFocused;
        previouslyFocused = null;
        if (target && document.body.contains(target) && typeof target.focus === 'function') {
          try { target.focus(); } catch { /* non-fatal */ }
        }
      }
    }

    if (!open) {
      try { onClose(); } catch { /* non-fatal */ }
    }
  }

  // Close on ×
  closeBtn.addEventListener('click', () => setOpen(false));

  // Close on Escape.  Listener mounted only while open to avoid
  // swallowing Esc elsewhere in the app.
  function handleKeydown(ev) {
    if (ev.key === 'Escape' && open) setOpen(false);
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('keydown', handleKeydown);
  }

  // --- Re-render on external settings changes ---
  //
  // Other parts of the app shouldn't be writing to settings while the
  // panel is open (the panel is the ONLY entry point per §1), but the
  // panel DOES need to re-sync its own controls when the user toggles
  // something (e.g. theme-grid after locale change updates the
  // numeralScript control).  We handle each control's local sync
  // inline above.

  return {
    panelElement,
    open()  { setOpen(true);  },
    close() { setOpen(false); },
    isOpen() { return open; },
    destroy() {
      // Opt out of focus restoration on destroy: when the locale-change
      // rebuild calls panel.destroy() then home.destroy(), restoring
      // focus to the about-to-be-removed gear button would flicker
      // through a stale element and produce a confusing screen-reader
      // announcement.  Clearing previouslyFocused makes setOpen's
      // restore branch a no-op.
      previouslyFocused = null;
      setOpen(false);
      if (fullscreenChangeHandler && typeof document !== 'undefined') {
        document.removeEventListener('fullscreenchange', fullscreenChangeHandler);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('keydown', handleKeydown);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
        // Family section's independent online/offline listeners.
        window.removeEventListener('online',  handleFamilyOnline);
        window.removeEventListener('offline', handleFamilyOffline);
      }
      if (panelElement.parentNode) {
        panelElement.parentNode.removeChild(panelElement);
      }
    },
    // Test hook.
    __getActiveThemes() { return getActiveThemes(); },
  };
}
